import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {MaybeArchivableItem} from 'archive/ArchiveConstants';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import {navigate} from 'BenchlingUtil';
import {isIntegrationField} from 'schemas/fields/integration/IntegrationFieldUtil';
import {SchemaField, SchemaFieldNameCallbackData} from 'schemas/fields/SchemaFieldConstants';
import {schemaInterfaceURL} from 'schemas/schema-interface/SchemaInterfaceDAO';
import getSchemaInterfaceIcon from 'schemas/schema-interface/v2/getSchemaInterfaceIcon';
import {getMessageForRelatedApps} from 'schemas/SchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {PURPLE} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FormulaIcon from 'ui-library/icons/FormulaIcon';
import InfoIcon from 'ui-library/icons/InfoIcon';
import APIUtil from 'util/APIUtil';

interface SchemaFieldNameProps {
  isCompact?: boolean;
  /**
   * Use of getSchemaFieldArchivableItem helper is expected before passing a field into this component.
   *
   * Note when working with this component that the `id` field is an alias for `apiId`, NOT the numeric
   * database ID.
   */
  field: Omit<SchemaField, 'id'> & MaybeArchivableItem;
  migratingFromFieldName?: string | null;
  migratingToFieldName?: string | null;
  onFieldNameClick?: (field: SchemaFieldNameCallbackData) => void;
  fieldNameTooltip?: string;
}

export default function SchemaFieldName({
  field,
  isCompact = true,
  migratingFromFieldName = null,
  migratingToFieldName = null,
  onFieldNameClick,
  fieldNameTooltip,
}: SchemaFieldNameProps): JSX.Element {
  const relatedBenchlingAppsMsg =
    field.relatedBenchlingApps != null ? getMessageForRelatedApps(field.relatedBenchlingApps) : null;

  const fieldNameElement = onFieldNameClick ? (
    <a
      onClick={(e) => {
        e.preventDefault();
        onFieldNameClick({apiId: field.apiId, name: field.name});
      }}
      className="u-primary-pointer"
      href="#"
    >
      {field.name}
    </a>
  ) : (
    <span>{field.name}</span>
  );

  const fieldNameWithTooltip = fieldNameTooltip ? (
    <Tooltip text={fieldNameTooltip}>{fieldNameElement}</Tooltip>
  ) : (
    fieldNameElement
  );

  return (
    <span
      {...getTestProps({component: 'SchemaFieldName', element: 'root'})}
      className={css(styles.schemaFieldName)}
    >
      {fieldNameWithTooltip}
      <ArchiveRecordLabel item={field} />
      {field.isComputed && (
        <Tooltip
          text={
            isCompact
              ? 'Computed Field'
              : 'This is a computed field. Please contact Benchling support for configuration.'
          }
        >
          <FormulaIcon className="u-faded u-mini-spacing-left" />
        </Tooltip>
      )}
      {field.isConvertedFromLink && (
        <Tooltip text="This read-only field represents the archived item from which this item was converted.">
          <BenchlingIcon className="u-faded u-mini-spacing-left" icon="converted-from" />
        </Tooltip>
      )}
      {field.relatedBenchlingApps != null && field.relatedBenchlingApps.length > 0 && (
        <Tooltip text={relatedBenchlingAppsMsg}>
          <BenchlingIcon className="u-faded u-mini-spacing-left" icon="integration" />
        </Tooltip>
      )}
      {field.appliedFieldset != null && (
        <Tooltip
          text={
            <div>
              This field is applied by{' '}
              {APIUtil.isStaticSchemaInterface(field.appliedFieldset.id) ? (
                <span>{field.appliedFieldset.name}</span>
              ) : (
                <a href={schemaInterfaceURL(field.appliedFieldset.id)} onClick={navigate}>
                  {field.appliedFieldset.name}
                </a>
              )}
            </div>
          }
        >
          <BenchlingIcon
            className="u-faded u-mini-spacing-left"
            icon={getSchemaInterfaceIcon(field.appliedFieldset.id)}
          />
        </Tooltip>
      )}
      {isIntegrationField(field) && (
        <Tooltip text="Value set by integration">
          <BenchlingIcon className="u-faded u-mini-spacing-left" icon="integration" />
        </Tooltip>
      )}
      {field.tooltipText && (
        <span className="u-faded u-mini-spacing-left">
          <Popover
            renderContent={() => <span>{field.tooltipText}</span>}
            placement={FloatingPlacement.TOP}
            interaction={FloatingInteraction.HOVER}
          >
            <InfoIcon />
          </Popover>
        </span>
      )}
      {migratingToFieldName && (
        <span className="u-mini-spacing-left">
          <Popover
            renderContent={() => (
              <span>
                This field is currently being migrated into "{migratingToFieldName}", and can't be edited
                right now. It will be archived once the new field is published.
              </span>
            )}
            placement={FloatingPlacement.BOTTOM}
            interaction={FloatingInteraction.HOVER}
          >
            <BenchlingIcon icon="lock" className={css(styles.purpleIcon)} />
          </Popover>
        </span>
      )}
      {migratingFromFieldName && (
        <span className="u-mini-spacing-left">
          <Popover
            renderContent={() => (
              <span>
                This field is being migrated from "{migratingFromFieldName}", and can be published once the
                migration is complete.
              </span>
            )}
            placement={FloatingPlacement.BOTTOM}
            interaction={FloatingInteraction.HOVER}
          >
            <BenchlingIcon icon="hourglass-half" className={css(styles.purpleIcon)} />
          </Popover>
        </span>
      )}
    </span>
  );
}

const styles = StyleSheet.create({
  schemaFieldName: {
    'text-wrap': 'balance',
  },
  purpleIcon: {
    color: PURPLE,
  },
});
